<script setup lang="ts">
import { DismissIcon, SearchIcon } from '@blpde/nx-icons';
import { computed, inject, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import type { BaseTextInputProps, InlineIconProps } from './internal';
import NxTextInput from './NxTextInput.vue';

// Every search field in the apps reads „Suche“ — not „Kanäle filtern“ or
// „Ordner durchsuchen“ — so the kit default is that word and apps pass none.
const props = withDefaults(
  defineProps<
    BaseTextInputProps & {
      placeholder?: string;
      /**
       * Opt-in trailing ✕ while there is text. A click or Escape (with text in
       * the field) clears it, emits `clear` and keeps the focus in the field.
       * That Escape is consumed, so a surrounding popover or dialog stays open;
       * Escape on an empty field passes through. Off by default.
       */
      clearable?: boolean;
    }
  >(),
  { placeholder: 'Suche', clearable: false }
);
const model = defineModel<string>({ required: true });
const emit = defineEmits<{
  /** The text was cleared through the ✕ or Escape (`update:modelValue` carries `''` too). */
  clear: [];
}>();

// A host without kit messages hands the key back, which is no label: fall back to German.
// The key is `search_input`, not `search`: the apps own `common.search` as a plain text and a
// kit object under it would replace theirs in every merged message table.
const CLEAR_KEY = 'common.search_input.clear';
const i18n = inject(i18nInjectionKey, null);
const clearLabel = computed(() => {
  const text = i18n?.t(CLEAR_KEY);
  return text && text !== CLEAR_KEY ? text : 'Löschen';
});

// Pass NxTextInput's handles through. It exposes them, but a `<script setup>`
// component exposes nothing unless it says so, so without this they stopped here
// and a caller could not focus the field (needed by the Stammdaten cockpit).
const textInputRef = useTemplateRef('text-input');
defineExpose({
  inputElement: computed(() => textInputRef.value?.inputElement ?? null),
  wrapperElement: computed(() => textInputRef.value?.wrapperElement ?? null),
});

const hasText = computed(() => model.value !== '');

function clearText() {
  model.value = '';
  emit('clear');
  // The ✕ never takes focus (its mousedown is prevented), so put it back in the
  // field to let typing start at once.
  textInputRef.value?.inputElement?.focus();
}

const icons = computed<InlineIconProps[]>(() => {
  const list: InlineIconProps[] = [
    { component: SearchIcon, align: 'start', filled: true, color: 'var(--nxt-fg-subtle)' },
  ];
  if (props.clearable && hasText.value) {
    list.push({
      component: DismissIcon,
      align: 'end',
      filled: true,
      size: '1.25em',
      color: 'var(--nxt-brand-bg)',
      label: clearLabel.value,
      tooltip: clearLabel.value,
      onClick: clearText,
    });
  }
  return list;
});

// A clearable field owns Escape (it clears); the field's own restore of the text from before the
// edit would put the discarded text back on the next Escape, so it stays off then.
function onEscape(event: KeyboardEvent) {
  // The Escape that clears is consumed (first press clears, the next one reaches a
  // surrounding popover); an empty field or a composition leaves Escape alone.
  if (!props.clearable || !hasText.value || event.isComposing) return;
  event.preventDefault();
  event.stopPropagation();
  clearText();
}
</script>

<template>
  <NxTextInput
    ref="text-input"
    v-model="model"
    :placeholder="placeholder"
    :height="height"
    :wrapper-style="wrapperStyle"
    :icons="icons"
    :escape-restores="!clearable"
    @keydown.esc="onEscape"
  />
</template>
